import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { Link, useParams } from "react-router-dom"
import {
  Accordion,
  Button,
  Card,
  Form,
  Icon,
  Input,
  Message,
  Modal,
  Tab,
  Table,
  TabPane,
  TextArea,
} from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { useThrottle } from "simplydo/react/hooks"
import ideasImage from "src/images/ideas.png"

import api from "api"
import { UserChip } from "components/lib/Chips"
import { EmptyBox } from "components/lib/UI"
import { useAppSelector } from "store"
import useTheme from "theme/useTheme"
import util from "utils/utils"

const aiAttachmentTypes = {
  pdf: "application/pdf",
  csv: "text/csv",
  txt: "text/plain",
  rtf: "text/rtf",
  md: "text/md",
  html: "text/html",
  xml: "text/xml",
}

type IIdeaTemplateChooser = {
  onTemplateSelected: (template: any) => void
}

type IChallengeSearchState = {
  page: number
  limit: number
  query: string
}

const IdeaTemplateChooser = ({ onTemplateSelected }: IIdeaTemplateChooser) => {
  const [_loading, setLoading] = useState<boolean>(false)
  const [aiModalOpen, setAiModalOpen] = useState(false)
  const [aiInput, setAiInput] = useState("")
  const [aiAttachments, setAiAttachments] = useState([])
  const [aiIsLoading, setAiIsLoading] = useState(false)
  const aiAttachmentInputRef = useRef<HTMLInputElement>(null)
  const [challenges, setChallenges] = useState<OpenAPI.Schemas["Challenge"][]>([])
  const [nextPageAvailable, setNextPageAvailable] = useState<boolean>(false)
  const [prevPageAvailable, setPrevPageAvailable] = useState<boolean>(false)
  const [accordionIndex, setAccordionIndex] = useState<number>(-1)
  const [selectedChallenge, setSelectedChallenge] = useState<string>("")
  const [challengeSearchState, setChallengeSearchState] = useState<IChallengeSearchState>({
    page: 1,
    limit: 9,
    query: "",
  })
  const params = useParams<{ id: string }>()
  const challenge = useAppSelector((state) => state.challenges.challenges.filter((c) => c._id === params.id)[0])
  const user = useAppSelector((state) => state.user)
  const canManageChallenge = useMemo(() => util.canManageChallenge(user, challenge), [user, challenge])
  const { t } = useTranslation()
  const theme = useTheme()
  const challengeId = params.id

  const templates = [
    {
      _id: "3is",
      name: 'The "3 Is"',
      description: `A short template designed to quickly collect ${t("generic.ideas")} for internal innovation. Includes a self-assessment rating against Importance, Impact, and ease of Implementation.`,
      image: util.getCdnFile("images/format_3is.png"),
      disableAttachments: true,
      body: [
        {
          name: `The ${t("generic.idea")}`,
          fields: [
            {
              wordLimit: "50",
              id: "overview",
              title: `What is your ${t("generic.idea")} about?`,
              description: `Briefly describe your ${t("generic.idea")} as you see it.`,
              type: "longtext",
            },
            {
              id: "importance",
              title: `How important is this ${t("generic.idea")} to you?`,
              description: "10 = Very Important",
              type: "slider",
            },
            {
              id: "implementation",
              title: "How easy would it be to implement?",
              description: "10 = Very easy",
              type: "slider",
            },
            {
              id: "impact",
              title: "How much impact could it have?",
              description: "10 = Game changing",
              type: "slider",
            },
            {
              id: "image",
              title: `Please upload an image of your ${t("generic.idea")} `,
              description: "Optional ",
              type: "image",
            },
          ],
        },
      ],
    },
    {
      _id: "scratch",
      name: "Start from scratch",
      description:
        "None of these suit? No problem - you can craft your own format from scratch using our format builder.",
      image: util.getCdnFile("images/format_scratch.png"),
      disableAttachments: true,
      body: [
        {
          name: `The ${t("generic.idea")}`,
          fields: [],
        },
      ],
    },
  ]

  const userId = user?._id
  const getChallenges = useThrottle(
    () => {
      setLoading(true)
      setSelectedChallenge("")
      api.users.getChallenges(
        userId,
        challengeSearchState,
        (data) => {
          setChallenges(data.challenges)
          setNextPageAvailable(data.nextPageAvailable)
          setPrevPageAvailable(data.prevPageAvailable)
          setLoading(false)
        },
        (err) => {
          toast.error(err.message)
          setLoading(false)
        },
      )
    },
    500,
    [userId, challengeSearchState],
  )

  useEffect(() => {
    getChallenges()
  }, [getChallenges])

  const onClickDefaultTemplate = useCallback(
    (template) => {
      onTemplateSelected(template)
      setSelectedChallenge(template._id)
    },
    [onTemplateSelected],
  )

  const onClickChallenge = useCallback(
    (clickedChallenge) => {
      setSelectedChallenge(clickedChallenge._id)
      api.challenges.getTemplate(
        clickedChallenge._id,
        (newTemplate) => {
          if (newTemplate.body) {
            onTemplateSelected(newTemplate)
          } else {
            toast.error("Challenge has no valid template.")
          }
        },
        () => {},
        null,
      )
    },
    [onTemplateSelected],
  )

  const handleAccordionClick = useCallback(
    (_e, titleProps) => {
      if (!titleProps) return
      const { index } = titleProps
      const newIndex = accordionIndex === index ? -1 : index
      setAccordionIndex(newIndex)
    },
    [accordionIndex],
  )

  return (
    <div>
      <h3>
        {!selectedChallenge ? <Icon name={"exclamation circle"} color={"red"} /> : null}
        {t("challenge.templateChooser.title")}
      </h3>
      <Modal
        open={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        size="small"
        mountNode={document.getElementById("semantic-modal-mount-node")}
      >
        <Modal.Header>Automatically generate a format</Modal.Header>
        <Modal.Content>
          <Tab
            panes={[
              {
                menuItem: "Describe your format",
                render: () => (
                  <TabPane>
                    <Form>
                      <div style={{ marginBottom: 16 }}>
                        SimplyDo's AI will generate a format for you based on the description and instructions you
                        provide below. The more precise you are, the more accurate your format will be.
                      </div>
                      <TextArea
                        placeholder="Describe your format here..."
                        value={aiInput}
                        onChange={(_e, { value }) => {
                          setAiInput(value as string)
                        }}
                      />
                    </Form>
                  </TabPane>
                ),
              },
              {
                menuItem: "or upload a file",
                render: () => (
                  <TabPane>
                    <div style={{ marginBottom: 16 }}>
                      Choose a PDF or CSV file containing the details about the format you want to generate.
                    </div>
                    <input
                      accept={Object.values(aiAttachmentTypes).join(",")}
                      style={{ display: "none" }}
                      ref={aiAttachmentInputRef}
                      type="file"
                      multiple
                      onChange={async (e) => {
                        const files = []

                        const toBase64 = async (file) =>
                          await new Promise((resolve, reject) => {
                            const reader = new FileReader()
                            reader.readAsDataURL(file)
                            reader.onload = () => {
                              const dataUrl = reader.result as string
                              const base64 = dataUrl.split(",")[1]
                              resolve(base64)
                            }
                            reader.onerror = reject
                          })

                        for (let i = 0; i < e.target.files.length; i++) {
                          const fileData = e.target.files[i]
                          const fileName = e.target.files[i].name
                          try {
                            const base64 = await toBase64(fileData)
                            files.push({
                              name: fileName,
                              type: fileData.type,
                              size: fileData.size,
                              data: base64,
                            })
                          } catch (_e) {
                            toast.error("Error uploading file")
                          }
                        }

                        setAiAttachments((prev) => [...prev, ...files])
                        setAiInput("Use the attached files to generate a format")
                      }}
                    />
                    <Button
                      icon="upload"
                      content="Upload attachments"
                      onClick={() => {
                        if (aiAttachmentInputRef.current) {
                          aiAttachmentInputRef.current.click()
                        }
                      }}
                    />
                    {aiAttachments.length > 0 && (
                      <Table>
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell>File name</Table.HeaderCell>
                            <Table.HeaderCell>Type</Table.HeaderCell>
                            <Table.HeaderCell>Actions</Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {aiAttachments.map((attachment, i) => (
                            <Table.Row key={i}>
                              <Table.Cell>{attachment.name}</Table.Cell>
                              <Table.Cell>{attachment.type}</Table.Cell>
                              <Table.Cell>
                                <Button
                                  basic
                                  icon="trash"
                                  onClick={() => {
                                    setAiAttachments((prev) => prev.filter((_, index) => index !== i))
                                    if (aiAttachments.length === 1) {
                                      setAiInput("")
                                    }
                                  }}
                                />
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    )}
                  </TabPane>
                ),
              },
            ]}
          />
          <Message info icon size="small">
            <Icon name="info circle" />
            By proceeding, you acknowledge that AI-generated content is not guaranteed to be 100% accurate. Please
            remember to double-check the generated format before launching your {t("generic.challenge")}.
          </Message>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setAiModalOpen(false)}>Cancel</Button>
          <Button
            primary
            disabled={!aiInput}
            onClick={() => {
              setAiIsLoading(true)
              api.challenges.genAIGenerateChallengeFormat(
                { prompt: aiInput, attachments: aiAttachments },
                (data) => {
                  setAiIsLoading(false)
                  if (data?.sections) {
                    onTemplateSelected({ body: data.sections })
                  } else {
                    toast.error("AI generation failed.")
                  }
                },
                (err) => {
                  setAiIsLoading(false)
                  toast.error(err.message)
                },
              )
            }}
            loading={aiIsLoading}
          >
            Generate
          </Button>
        </Modal.Actions>
      </Modal>
      <Card.Group itemsPerRow={3} stackable>
        {templates.map((template, i) => {
          // Temporarily disable challenge capture for regular users/admins
          if (template._id === "challengeCapture" && !util.hasPermission(user, "super.viewDashboard")) return null
          return (
            <Card key={i}>
              {template.image && (
                <div
                  style={{
                    height: 150,
                    backgroundPosition: "center center",
                    backgroundSize: "cover",
                    backgroundImage: `url(${template.image})`,
                  }}
                />
              )}
              <Card.Content>
                <Card.Header>{template.name}</Card.Header>
                <Card.Meta>{template.description}</Card.Meta>
              </Card.Content>
              <Card.Content extra>
                <Button
                  fluid
                  disabled={
                    !canManageChallenge && !util.hasPermission(user, "challenge.editIdeaFormat", challenge?._id)
                  }
                  primary
                  title={template.name}
                  content="Use this format"
                  onClick={() => onClickDefaultTemplate(template)}
                />
              </Card.Content>
            </Card>
          )
        })}
        {(util.organisationFeaturesEnabled(user, ["aiGenChallengeFormat"]) ||
          util.hasPermission(user, "super.viewDashboard")) && (
          <Card>
            <div
              style={{
                height: 150,
                backgroundPosition: "center center",
                backgroundSize: "cover",
                backgroundImage: `url(${ideasImage})`,
              }}
            />
            <Card.Content>
              <Card.Header>Automatically Generated</Card.Header>
              <Card.Meta>
                Describe your format with text or files, and SimplyDo will automatically generate a format for you.
              </Card.Meta>
            </Card.Content>
            <Card.Content extra>
              <Button
                fluid
                disabled={!canManageChallenge && !util.hasPermission(user, "challenge.editIdeaFormat", challenge?._id)}
                primary
                content="Get started"
                onClick={() => setAiModalOpen(true)}
              />
            </Card.Content>
          </Card>
        )}
      </Card.Group>

      {challenges.length > 0 ||
      (!!challengeSearchState.query &&
        (canManageChallenge || util.hasPermission(user, "challenge.editIdeaFormat", challenge?._id))) ? (
        <Accordion style={{ marginTop: 20 }}>
          <Accordion.Title active={accordionIndex === 0} index={0} onClick={handleAccordionClick}>
            <h4>
              <Icon name="dropdown" />
              Re-use a format from one of your existing {t("generic.challenges")}
            </h4>
          </Accordion.Title>
          <Accordion.Content active={accordionIndex === 0} index={0} onClick={handleAccordionClick}>
            <Input
              placeholder={`Search for ${t("generic.challengeWithArticle")}..`}
              onChange={(_e, { value }) =>
                setChallengeSearchState((prevState) => ({ ...prevState, page: 1, query: value }))
              }
              style={{ marginBottom: 10 }}
              value={challengeSearchState.query}
            />
            {challenges?.length ? (
              <Card.Group itemsPerRow={theme.sizes.isComputer ? 3 : 2} centered>
                {challenges
                  .filter((c) => c._id !== challengeId)
                  .map((c) => (
                    <Card key={c._id}>
                      <div
                        style={{
                          height: 150,
                          backgroundPosition: "center center",
                          backgroundSize: "cover",
                          backgroundImage: `url(${util.mixinCssUrlFallback(util.challengeImage(c), util.challengeImage())})`,
                        }}
                      />
                      <Card.Content>
                        <Card.Header>{c.name}</Card.Header>
                        <Card.Meta>{t("challenge.templateChooser.copyFromChallenge")}</Card.Meta>
                        <Card.Meta style={{ marginTop: 10 }}>
                          <Link className="with-border" to={`/challenges/${c._id}/format`} target="_blank">
                            View format
                          </Link>
                        </Card.Meta>
                      </Card.Content>
                      <Card.Content extra>
                        {/* @ts-ignore */}
                        <UserChip user={{ ...user, emails: null }} />
                      </Card.Content>
                      <Card.Content extra>
                        <Button fluid primary content="Use this format" onClick={() => onClickChallenge(c)} />
                      </Card.Content>
                    </Card>
                  ))}
              </Card.Group>
            ) : (
              <EmptyBox title={undefined} message={undefined} style={undefined}>
                No {t("generic.challenges")} found
              </EmptyBox>
            )}
            {nextPageAvailable || prevPageAvailable ? (
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 10,
                }}
              >
                {prevPageAvailable ? (
                  <Button
                    size="small"
                    content="Prev page"
                    onClick={() =>
                      setChallengeSearchState((prevState) => ({
                        ...prevState,
                        page: prevState.page - 1,
                      }))
                    }
                  />
                ) : (
                  <div />
                )}
                {nextPageAvailable ? (
                  <Button
                    size="small"
                    content="Next page"
                    onClick={() =>
                      setChallengeSearchState((prevState) => ({
                        ...prevState,
                        page: prevState.page + 1,
                      }))
                    }
                  />
                ) : null}
              </div>
            ) : null}
          </Accordion.Content>
        </Accordion>
      ) : null}
    </div>
  )
}

const mapStateToProps = (state) => ({ user: state.user })

export default connect(mapStateToProps)(IdeaTemplateChooser)
